"use client";

import Link from "next/link";
import {
  Bus,
  Truck,
  ClipboardCheck,
  Wifi,
  LogOut,
  Wrench,
  ClipboardList,
  Box,
  Sun,
  Zap,
  ZapOff,
  Shield,
  Activity,
  Calendar,
  Settings2,
  Fuel,
  ChevronRight,
  UserCheck,
  MapPin,
  Building2
} from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";

const MODULE_CATEGORIES = [
  {
    categoryTitle: "Operaciones y Flota",
    modules: [
      {
        title: "Hoja de Cuadros",
        path: "/hoja-cuadros",
        icon: ClipboardList,
        description: "Gestión de asignación de choferes a unidades activas."
      },
      {
        title: "Agenda General",
        path: "/agenda-general",
        icon: Calendar,
        description: "Calendario de operación y asignación por áreas."
      },
      {
        title: "Parque Vehicular",
        path: "/parque",
        icon: Bus,
        description: "Control técnico e inventario de la flota vehicular."
      },
      {
        title: "Registro Entrada/Sal",
        path: "/registro",
        icon: LogOut,
        description: "Bitácora operativa de entrada y salida del taller."
      },
      {
        title: "Viajes",
        path: "/viajes",
        icon: MapPin,
        description: "Catálogo de plantas, rutas y asignación diaria."
      },
      {
        title: "Módulo Supervisor",
        path: "/viajes/supervisor",
        icon: Building2,
        description: "Gestión y asignaciones por plantas autorizadas."
      }
    ]
  },
  {
    categoryTitle: "Mantenimiento Técnico",
    modules: [
      {
        title: "Mantenimiento",
        path: "/mantenimiento",
        icon: Wrench,
        description: "Gestión de preventivos, especiales e inspección técnica."
      },
      {
        title: "Calibración",
        path: "/calibracion",
        icon: Activity,
        description: "Registro de calibraciones técnicas de equipos y herramientas."
      },
      {
        title: "Gestión de Llantas",
        path: "/llantas",
        icon: Wrench,
        description: "Control exhaustivo de desgaste y cambio de neumáticos."
      },
      {
        title: "Campañas",
        path: "/campanias",
        icon: ClipboardCheck,
        description: "Vigilancia de campañas de seguridad y reparaciones masivas."
      }
    ]
  },
  {
    categoryTitle: "Suministros y Logística",
    modules: [
      {
        title: "Cargas Diesel",
        path: "/diesel",
        icon: Fuel,
        description: "Registro de cargas de diesel por gasolinera y unidad."
      },
      {
        title: "Cargas AdBlue",
        path: "/adblue",
        icon: Bus,
        description: "Gestión de carga y rendimiento de AdBlue por unidad."
      },
      {
        title: "Inventario",
        path: "/inventario",
        icon: Box,
        description: "Control de almacén de refacciones y consumibles."
      },
      {
        title: "Herramientas y Eq",
        path: "/herramientas",
        icon: Settings2,
        description: "Inventario y mantenimiento de herramientas y equipo de taller."
      },
      {
        title: "Arrastres",
        path: "/arrastres",
        icon: Truck,
        description: "Control logístico de servicios de grúa y arrastre."
      },
      {
        title: "WIFI",
        path: "/wifi",
        icon: Wifi,
        description: "Credenciales y estado técnico de equipos WIFI a bordo."
      }
    ]
  }
];

export default function Home() {
  const { toggleDarkMode, animationsEnabled, toggleAnimations } = useTheme();
  const { user, isAdmin, hasModuleAccess } = useAuth();

  return (
    <div className="min-h-screen bg-[#F8F9FA] dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        
        {/* Top Navigation Header */}
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-8 mb-8 border-b-2 border-gray-300 dark:border-slate-800">
          <div>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[#D96B27] text-white flex items-center justify-center font-bold shadow-md">
                <Bus className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
                  GESTIÓN DE TALLER Y FLOTA
                </h1>
                <p className="text-sm md:text-base font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mt-0.5">
                  Autotransportes Latinoamericanos
                </p>
              </div>
            </div>

            {/* Connection Status Badge */}
            {user && (
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-900 border-2 border-gray-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm font-bold rounded-xl shadow-sm">
                  <UserCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  {user.username}
                </span>
                {isAdmin && (
                  <span className="px-3.5 py-1.5 bg-[#D96B27]/10 text-[#D96B27] dark:text-[#f38a4a] text-sm font-black uppercase rounded-xl border-2 border-[#D96B27]/30">
                    Administrador
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Quick Action Controls */}
          <div className="flex items-center gap-3 self-end md:self-auto">
            <button
              onClick={toggleAnimations}
              className="p-3.5 rounded-2xl bg-white dark:bg-slate-900 border-2 border-gray-300 dark:border-slate-700 shadow-sm hover:border-[#D96B27] transition-all text-slate-800 dark:text-slate-200"
              title={animationsEnabled ? "Desactivar animaciones" : "Activar animaciones"}
            >
              {animationsEnabled ? <Zap className="w-7 h-7 text-amber-500" /> : <ZapOff className="w-7 h-7 text-gray-400" />}
            </button>

            <button
              onClick={toggleDarkMode}
              className="p-3.5 rounded-2xl bg-white dark:bg-slate-900 border-2 border-gray-300 dark:border-slate-700 shadow-sm hover:border-[#D96B27] transition-all text-slate-800 dark:text-slate-200"
              title="Cambiar Tema"
            >
              <Sun className="w-7 h-7 text-amber-500 dark:text-slate-300" />
            </button>
          </div>
        </header>

        {/* Categorized Modules Grid */}
        <main className="space-y-12">
          {MODULE_CATEGORIES.map((category, catIdx) => {
            const accessibleModules = category.modules.filter((m) =>
              hasModuleAccess(m.path)
            );

            if (accessibleModules.length === 0) return null;

            return (
              <section key={catIdx}>
                <h2 className="text-sm md:text-base font-black uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-5 px-1">
                  {category.categoryTitle}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {accessibleModules.map((module, modIdx) => (
                    <Link key={modIdx} href={module.path} className="group">
                      <div className="h-full bg-white dark:bg-slate-900/90 p-6 rounded-2xl border-2 border-gray-200 dark:border-slate-800 shadow-sm hover:shadow-lg hover:border-[#D96B27] transition-all duration-200 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <div className="p-3.5 rounded-2xl bg-[#D96B27]/10 text-[#D96B27] group-hover:bg-[#D96B27] group-hover:text-white transition-colors duration-200">
                              <module.icon className="w-7 h-7 stroke-[2.2]" />
                            </div>
                            <ChevronRight className="w-6 h-6 text-gray-400 group-hover:text-[#D96B27] transition-colors" />
                          </div>
                          
                          <h3 className="font-extrabold text-slate-900 dark:text-white text-xl group-hover:text-[#D96B27] transition-colors leading-snug">
                            {module.title}
                          </h3>
                          <p className="text-base text-slate-700 dark:text-slate-300 font-medium mt-2 leading-relaxed">
                            {module.description}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            );
          })}

          {/* Admin Restricted Access Section */}
          {isAdmin && (
            <section className="pt-4">
              <h2 className="text-sm md:text-base font-black uppercase tracking-wider text-[#D96B27] mb-5 px-1">
                Panel Administrativo
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                <Link href="/admin" className="group">
                  <div className="h-full bg-orange-50/60 dark:bg-slate-900 p-6 rounded-2xl border-2 border-dashed border-[#D96B27] hover:border-[#D96B27] shadow-sm transition-all duration-200 flex items-start gap-4">
                    <div className="p-3.5 rounded-2xl bg-[#D96B27] text-white shadow-sm shrink-0">
                      <Shield className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="font-extrabold text-slate-900 dark:text-white text-xl group-hover:text-[#D96B27] transition-colors">
                        Configuración
                      </h3>
                      <p className="text-base text-slate-700 dark:text-slate-300 font-medium mt-1 leading-relaxed">
                        Control de permisos, usuarios y parámetros del sistema.
                      </p>
                    </div>
                  </div>
                </Link>
              </div>
            </section>
          )}
        </main>

        {/* System Footer */}
        <footer className="mt-20 pt-8 border-t-2 border-gray-200 dark:border-slate-800 text-center">
          <p className="text-sm font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
            &copy; {new Date().getFullYear()} Autotransportes Latinoamericanos — Sistema de Gestión Operativa
          </p>
        </footer>
      </div>
    </div>
  );
}